import {
  createRouter,
  createWebHistory,
  createWebHashHistory,
} from "vue-router";

import BookingHome from "@views/BookingHome.vue";
import Hero from "@app/Hero.vue";
// Import auth service to check login status
import { useBookingStore } from "@stores/BookingStore"; // Import the booking store
import { isLoggedIn, checkWordPressLoginStatus } from "@services/authService";

const router = createRouter({
  history: createWebHistory("/app/"),
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top
    return { top: 0 };
  },
  routes: [
    // {
    //   path: "/",
    //   name: "home",
    //   component: Hero,
    //   meta: { title: "Home" }
    // },
    {
      path: "/",
      redirect: { name: "booking" },
      // No title needed here as it redirects immediately.
      // The target route 'booking' will set its title.
    },
    {
      path: "/booking",
      name: "booking",
      component: BookingHome,
      meta: {
        requiresAuth: false,
        breadcrumb: "Search Results",
        title: "Find Clinic", // Example title
      },
    },

    {
      path: "/booking/clinic-profile",
      name: "clinic-profile",
      component: () => import("@views/ClinicProfile.vue"),
      meta: {
        requiresAuth: false, // Adjust as needed
        breadcrumb: "Clinic Profile",
        title: "Clinic Profile",
      },
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@views/Login.vue"),
      meta: {
        requiresAuth: false, // Login page itself doesn't require auth
        // breadcrumb: "Login" // Often, breadcrumbs are not shown on login pages
        title: "Login",
      },
    },
    {
      path: "/booking/summary",
      name: "summary",
      component: () => import("@views/Summary.vue"),
      meta: {
        requiresAuth: true, // Summary page requires login
        breadcrumb: "Booking Summary",
        title: "Booking Summary",
      },
    },
    {
      path: "/booking/payment",
      name: "payment",
      component: () => import("@views/Payment.vue"),
      meta: {
        requiresAuth: true, // Summary page requires login
        breadcrumb: "Payment",
        title: "Payment",
      },
    },
    {
      path: "/booking/confirmation",
      name: "confirmation",
      component: () => import("@views/Confirmation.vue"),
      meta: {
        requiresAuth: true, // Confirmation likely requires login
        breadcrumb: "Booking Confirmation",
        title: "Booking Confirmation",
      },
    },
    {
      path: "/dashboard/appointments",
      name: "appointments",
      component: () => import("@views/Appointments.vue"),
      meta: {
        requiresAuth: true, // Appointments page requires login
        breadcrumb: "My Appointments",
        title: "My Appointments",
      },
    },
    {
      path: "/dashboard/settings",
      name: "settings",
      component: () => import("@views/Settings.vue"),
      meta: {
        requiresAuth: true, // Settings page requires login
        breadcrumb: "Account Settings",
        title: "Account Settings",
      },
    },
    {
      path: "/dashboard/appointment/details",
      name: "details",
      component: () => import("@views/AppointmentDetails.vue"),
      meta: {
        requiresAuth: true, // Appointment details likely require login
        breadcrumb: "Appointment Details",
        // If this route had a parameter like /details/:id, you could do:
        // breadcrumb: route => `Appointment ${route.params.id}`
        title: "Appointment Details", // Can be made dynamic if needed, e.g., route => `Details for ${route.query.booking_id}`
      },
    },
    {
      path: "/dashboard/notifications",
      name: "notifications",
      component: () => import("@views/Notifications.vue"),
      meta: {
        requiresAuth: true, // Notifications page requires login
        breadcrumb: "Notifications",
        title: "Notifications",
      },
    },
  ],
});

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // If isLoggedIn is false, and we haven't checked the server yet in this session,
  // or if the 'to' route requires auth and we are not sure about login status.
  // We can add a flag to avoid re-checking on every navigation if already checked once.
  // For simplicity here, we check if the reactive state is false.
  if (!isLoggedIn.value) {
    try {
      // Attempt to verify/recover session from server cookies
      await checkWordPressLoginStatus();
    } catch (error) {
      // Log error but don't necessarily block navigation unless it's critical
      // for non-auth routes. The checkWordPressLoginStatus itself handles
      // setting isLoggedIn to false on error.
      console.warn(
        "Initial auth check failed during navigation:",
        error.message
      );
    }
  }

  // After attempting to check/update login status, make the navigation decision
  if (requiresAuth && !isLoggedIn.value) {
    // User is not logged in and the route requires authentication
    console.log(
      `Navigation guard: Route ${to.path} requires auth, user not logged in. Redirecting to login.`
    );
    next({ name: "login", query: { redirect: to.fullPath } });
  } else {
    // User is logged in OR the route does not require authentication
    next();
  }
});

// Global navigation guard to save booking state after each navigation
router.afterEach(async (to, from) => {
  const bookingStore = useBookingStore();
  // Only save if the store has been initialized
  if (
    bookingStore &&
    typeof bookingStore.saveBookingToLocalStorage === "function"
  ) {
    bookingStore.saveBookingToLocalStorage();
  }

  // Update document title
  const defaultSiteName = "Travel Jab"; // Your site's default name
  // Find the deepest matched route record with a 'title' meta field
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.title);

  if (nearestWithTitle && nearestWithTitle.meta.title) {
    let pageTitle = nearestWithTitle.meta.title;
    if (typeof pageTitle === "function") {
      pageTitle = pageTitle(to); // Allow dynamic titles based on route params/query
    }
    document.title = `${pageTitle} - ${defaultSiteName}`;
  } else {
    // Fallback title if no route-specific title is found
    // You could use the initial WordPress page title if passed via wp_localize_script
    document.title = defaultSiteName;
  }
});

export default router;
